import { FC, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Page } from 'app/modules/insights/types/Page';
import { useBestPostsForAllMetricsQuery } from 'app/modules/insights/modules/postsPerformance/hooks/useBestPostsForAllMetricsQuery';
import { getMedalRankFromIndex } from 'app/modules/insights/utils/getMedalRankFromIndex';
import StandardPostPreview from 'app/modules/insights/components/StandardPostPreview';
import { getMetricValue } from 'app/modules/insights/utils/getMetricValue';
import { useInsightsContainer } from 'components/insights/context';
import { getSupportedPostTypesForMetric } from 'app/modules/insights/modules/postsPerformance/utils/getSupportedPostTypesForMetric';
import { Tabs } from '@kontentino/ui';
import { ANALYTICS_LITE_POST_PERFORMANCE } from '../utils/postsPerformanceMetrics';
import { getMetricsForPageType } from 'app/modules/insights/modules/postsPerformance/utils/metricProperty';
import InsightsLiteConfig from 'app/config/InsightsLiteConfig';
import Skeleton from 'react-loading-skeleton';

type Props = {
  page: Page;
};

const InsightLitePostsPerformance: FC<Props> = ({ page }) => {
  const { t } = useTranslation();
  const { filterState } = useInsightsContainer();
  const metrics = useMemo(
    () => ANALYTICS_LITE_POST_PERFORMANCE[page._type] || [],
    [page._type],
  );
  const [activeMetric, setActiveMetric] = useState(metrics[0]?.key);

  useEffect(() => {
    if (metrics[0]?.key) {
      setActiveMetric(metrics[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page._type]);

  const filterKey = `${filterState.startDate.toString()}-${filterState.endDate.toString()}`;

  const currentMetric = getMetricsForPageType(page._type)[activeMetric];

  const maxPostsPerMetric = InsightsLiteConfig.MAX_POSTS_PER_METRIC;

  const queryVariables = useMemo(
    () => ({
      page,
      startDate: filterState.startDate,
      endDate: filterState.endDate,
      filterKey,
      metric:
        currentMetric || getMetricsForPageType(page._type)[metrics[0]?.key],
      types: getSupportedPostTypesForMetric(activeMetric, page._type),
      first: maxPostsPerMetric,
    }),
    [
      page,
      filterState.startDate,
      filterState.endDate,
      activeMetric,
      currentMetric,
      metrics,
      filterKey,
      maxPostsPerMetric,
    ],
  );

  const { posts, loading, error } =
    useBestPostsForAllMetricsQuery(queryVariables);

  if (!currentMetric) {
    return null;
  }

  return (
    <div className="tw-space-y-8">
      <Tabs.Root
        defaultValue={metrics[0]?.key}
        onValueChange={setActiveMetric}
        value={activeMetric}
      >
        <Tabs.List>
          {metrics.map((metric) => (
            <Tabs.Tab
              key={metric.key}
              data-name={`insights-lite_tab-${metric.key}`}
              value={metric.key}
            >
              {metric.title}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs.Root>

      {loading ? (
        <div className="tw-flex tw-flex-row tw-gap-4 tw-overflow-x-auto tw-pb-2">
          {Array.from({ length: InsightsLiteConfig.MAX_POSTS_PER_METRIC }).map(
            (_, index) => (
              <Skeleton
                key={index}
                className="tw-h-[400px] tw-w-[300px] tw-min-w-[300px]"
                containerClassName="tw-rounded tw-bg-grayscale-20"
              />
            ),
          )}
        </div>
      ) : error || !posts || posts.length === 0 ? (
        <div className="tw-rounded tw-bg-grayscale-20 tw-p-4 tw-text-center tw-text-grayscale-100">
          {t('insightsLite.noPostsAvailableForThisMetric')}
        </div>
      ) : (
        <div
          key={`${activeMetric}-${filterKey}`}
          className="tw-flex tw-gap-4 tw-overflow-x-auto tw-pb-2"
        >
          {posts.map(({ post, metrics }, index) => (
            <StandardPostPreview
              key={index}
              post={post}
              allowVideoPlayback={false}
              medalRank={getMedalRankFromIndex(index)}
              sizes={{ avatar: 28, socialIcon: 16 }}
              classNames={{
                root: 'tw-h-[400px] tw-w-[300px] tw-min-w-[300px] tw-border tw-border-grayscale-20',
                content: 'tw-gap-2 tw-p-3',
              }}
              metrics={[
                {
                  ...currentMetric,
                  value: getMetricValue(metrics, currentMetric),
                },
              ]}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default InsightLitePostsPerformance;
